import React, { useState, useContext } from "react";
import { Button, message } from "antd";
import { LockOutlined, UserOutlined, SafetyOutlined } from "@ant-design/icons";
import { QRCodeSVG } from "qrcode.react";
import Form from "ui/Forms/Form";
import FormItem from "ui/Forms/FormItem";
import {
  setAccessToken,
  setIdToken,
  setRefreshToken,
  handleUserLevel,
} from "features/identity/authHelper";
import { errorResponseMessages } from "api/apiErrorHelper";
import { UserContext } from "features/identity/userContext";
import { Input, InputPassword } from "ui/Inputs/Inputs";
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from "router/Router.config";
import { Link, useHistory } from "react-router-dom";
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import styles from "./Layout.module.scss";
import UserPool from "../UserPool";

type Stage = "credentials" | "mfa-setup" | "mfa-verify";

const TOTP_ISSUER = "II Admin Panel";

const buildOtpAuthUrl = (secret: string, account: string) => {
  const issuer = encodeURIComponent(TOTP_ISSUER);
  const label = encodeURIComponent(`${TOTP_ISSUER}:${account}`);
  return `otpauth://totp/${label}?secret=${secret}&issuer=${issuer}`;
};

const Login = () => {
  const { setUser } = useContext(UserContext);
  const history = useHistory();

  const [loading, setLoading] = useState<boolean>(false);
  const [isFirstLogin, setIsFirstLogin] = useState<boolean>(false);

  const [stage, setStage] = useState<Stage>("credentials");
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const [email, setEmail] = useState<string>("");
  const [totpSecret, setTotpSecret] = useState<string>("");

  const finalizeLogin = (data: CognitoUserSession, user: CognitoUser) => {
    setAccessToken(data.getAccessToken().getJwtToken());
    setIdToken(data.getIdToken().getJwtToken());
    setRefreshToken(data.getRefreshToken().getToken());
    setUser({
      name: user.getUsername(),
      authenticated: true,
      email: user.getUsername(),
    });
    handleUserLevel(user);
    message.info("Logged in successfully!");
    history.push(PRIVATE_ROUTES.APP_USERS_SCREEN.path);
  };

  const resetToCredentials = () => {
    setStage("credentials");
    setCognitoUser(null);
    setTotpSecret("");
    setEmail("");
    setLoading(false);
  };

  const beginTotpSetup = (user: CognitoUser) => {
    user.associateSoftwareToken({
      associateSecretCode: (secretCode: string) => {
        setTotpSecret(secretCode);
        setStage("mfa-setup");
        setLoading(false);
      },
      onFailure: (err) => {
        console.error("associateSoftwareToken error", err);
        errorResponseMessages(err);
        resetToCredentials();
      },
    });
  };

  const onCredentialsSubmit = (formData: {
    email: string;
    password: string;
    new_password?: string;
  }) => {
    setLoading(true);
    const { password, new_password: newPassword } = formData;
    const userEmail = formData.email;

    const user = new CognitoUser({
      Username: userEmail,
      Pool: UserPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: userEmail,
      Password: password,
    });

    user.authenticateUser(authDetails, {
      onSuccess: (data) => {
        setLoading(false);
        finalizeLogin(data, user);
      },
      onFailure: (error) => {
        console.error("authenticate error", error);
        setLoading(false);
        errorResponseMessages(error);
      },
      mfaSetup: () => {
        setCognitoUser(user);
        setEmail(userEmail);
        beginTotpSetup(user);
      },
      totpRequired: () => {
        setCognitoUser(user);
        setEmail(userEmail);
        setStage("mfa-verify");
        setLoading(false);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        if (newPassword) {
          delete userAttributes.email_verified;
          delete userAttributes.email;
          requiredAttributes.phone_number_verified =
            userAttributes.userAttributes;
          requiredAttributes.phone_number = userAttributes.phone_number;
          delete requiredAttributes.phone_number;
          delete requiredAttributes.phone_number_verified;

          user.completeNewPasswordChallenge(newPassword, requiredAttributes, {
            onSuccess: (data) => {
              setIsFirstLogin(false);
              setLoading(false);
              finalizeLogin(data, user);
            },
            onFailure: (error) => {
              console.error("completeNewPasswordChallenge error", error);
              setLoading(false);
              message.error("Something went wrong. Please try again.");
            },
            mfaSetup: () => {
              setIsFirstLogin(false);
              setCognitoUser(user);
              setEmail(userEmail);
              beginTotpSetup(user);
            },
          });
        } else {
          setIsFirstLogin(true);
          message.info("Please update your password.");
          setLoading(false);
        }
      },
    });
  };

  const onMfaSetupSubmit = (formData: { code: string }) => {
    if (!cognitoUser) {
      resetToCredentials();
      return;
    }
    setLoading(true);
    cognitoUser.verifySoftwareToken(formData.code.trim(), "authenticator", {
      onSuccess: (data) => {
        setLoading(false);
        finalizeLogin(data, cognitoUser);
      },
      onFailure: (error) => {
        console.error("verifySoftwareToken error", error);
        setLoading(false);
        message.error(error?.message || "Invalid code. Please try again.");
      },
    });
  };

  const onMfaVerifySubmit = (formData: { code: string }) => {
    if (!cognitoUser) {
      resetToCredentials();
      return;
    }
    setLoading(true);
    cognitoUser.sendMFACode(
      formData.code.trim(),
      {
        onSuccess: (data) => {
          setLoading(false);
          finalizeLogin(data, cognitoUser);
        },
        onFailure: (error) => {
          console.error("sendMFACode error", error);
          setLoading(false);
          message.error(error?.message || "Invalid code. Please try again.");
        },
      },
      "SOFTWARE_TOKEN_MFA"
    );
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo.errorFields);
  };

  if (stage === "mfa-setup") {
    const otpUrl = buildOtpAuthUrl(totpSecret, email);
    return (
      <Form
        name="mfaSetupForm"
        onFinish={onMfaSetupSubmit}
        onFinishFailed={onFinishFailed}
      >
        <p>
          Scan this QR code with Google Authenticator (or any TOTP app), then
          enter the 6-digit code to finish setup.
        </p>

        <div className={styles.qrWrapper}>
          <QRCodeSVG value={otpUrl} size={180} includeMargin />
        </div>

        <p className={styles.secretLabel}>
          Can&apos;t scan? Enter this key manually:
        </p>
        <code className={styles.secretCode}>{totpSecret}</code>

        <FormItem
          name={"code"}
          key={"code"}
          rules={[
            {
              required: true,
              message: "Please enter the 6-digit code",
            },
            {
              pattern: /^\d{6}$/,
              message: "Code must be 6 digits",
            },
          ]}
        >
          <Input
            prefix={<SafetyOutlined />}
            placeholder={"6-digit code"}
            size={"large"}
            maxLength={6}
            inputMode="numeric"
            autoComplete="one-time-code"
          />
        </FormItem>

        <FormItem>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={loading}
          >
            Verify and continue
          </Button>
        </FormItem>

        <div className={styles.links}>
          <Button type="link" onClick={resetToCredentials} disabled={loading}>
            Back to login
          </Button>
        </div>
      </Form>
    );
  }

  if (stage === "mfa-verify") {
    return (
      <Form
        name="mfaVerifyForm"
        onFinish={onMfaVerifySubmit}
        onFinishFailed={onFinishFailed}
      >
        <p>Enter the 6-digit code from your authenticator app to continue.</p>

        <FormItem
          name={"code"}
          key={"code"}
          rules={[
            {
              required: true,
              message: "Please enter the 6-digit code",
            },
            {
              pattern: /^\d{6}$/,
              message: "Code must be 6 digits",
            },
          ]}
        >
          <Input
            prefix={<SafetyOutlined />}
            placeholder={"6-digit code"}
            size={"large"}
            maxLength={6}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
          />
        </FormItem>

        <FormItem>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={loading}
          >
            Verify
          </Button>
        </FormItem>

        <div className={styles.links}>
          <Button type="link" onClick={resetToCredentials} disabled={loading}>
            Back to login
          </Button>
        </div>
      </Form>
    );
  }

  return (
    <Form
      name="loginForms"
      initialValues={{
        remember: true,
      }}
      onFinish={onCredentialsSubmit}
      onFinishFailed={onFinishFailed}
    >
      <FormItem
        name={"email"}
        key={"email"}
        rules={[
          {
            required: true,
            message: "Please input your e-mail!",
          },
        ]}
      >
        <Input
          type={"email"}
          prefix={<UserOutlined />}
          placeholder={"E-mail"}
          size={"large"}
        />
      </FormItem>

      <FormItem
        name={"password"}
        key={"password"}
        rules={[
          {
            required: true,
            message: "Please input your password!",
          },
        ]}
      >
        <InputPassword
          prefix={<LockOutlined />}
          placeholder={"Password"}
          size="large"
        />
      </FormItem>

      {isFirstLogin && (
        <FormItem
          name={"new_password"}
          key={"new_password"}
          rules={[
            {
              required: true,
              message: "Please input your new password!",
            },
          ]}
        >
          <InputPassword
            prefix={<LockOutlined />}
            placeholder={"New password"}
            size="large"
          />
        </FormItem>
      )}

      <div className={styles.links}>
        <Link to={PUBLIC_ROUTES.FORGOT_PASSWORD_SCREEN.path}>
          Forgot password?
        </Link>
        {/* <Link to={PUBLIC_ROUTES.SIGN_UP_SCREEN.path}>Sign up</Link> */}
      </div>

      <FormItem>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={loading}
        >
          Login
        </Button>
      </FormItem>
    </Form>
  );
};

export default Login;
